import { useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { createSelector } from "reselect"
import { Link } from "react-router-dom"
import { Card, CardBody, Col, Container, Input, Row, Spinner } from "reactstrap"
import withRouter from "Components/Common/withRouter"
import Breadcrumb from "Components/organisms/breadcrumb"
import { ManualsSliceState, setManuals } from "slices/manuals/reducer"
import { PermissionsSliceState } from "slices/auth/permissions/reducer"
import { getManuals } from "slices/manuals/thunk"
import { ManualMeta } from "models/manual"
import Screen from "models/screen"

const selectManuals = createSelector(
    (state: any) => state.Manuals,
    ({ manuals, isLoaded }: ManualsSliceState) => ({ manuals, isLoaded })
)

const selectScreens = createSelector(
    (state: any) => state.Permissions,
    ({ screens }: PermissionsSliceState) => screens
)

export const manualViewUrl = (slug: string) => `/manuals/${slug}`

// Manuals for pages everyone has (Dashboard, Profile, guides) come first, like Dashboard in the sidebar
const GENERAL_ORDER = -1
const UNKNOWN_ORDER = Number.MAX_SAFE_INTEGER

/** Position of a manual in the sidebar: the first of its screens in the user's screen list */
const sidebarOrder = (manual: ManualMeta, screens: Screen[]): number => {
    if (manual.screens.length === 0) return GENERAL_ORDER
    const index = screens.findIndex(s => manual.screens.includes(s.code))
    return index === -1 ? UNKNOWN_ORDER : index
}

const ManualsPage = () => {
    document.title = "Manuals | IbexIQ"

    const dispatch = useDispatch()
    const { manuals, isLoaded } = useSelector(selectManuals)
    const screens = useSelector(selectScreens)
    const [isLoading, setIsLoading] = useState(false)
    const [search, setSearch] = useState("")

    useEffect(() => {
        const load = async () => {
            setIsLoading(true)
            const data = await getManuals()
            setIsLoading(false)
            if (data) dispatch(setManuals(data))
        }
        load()
    }, [dispatch])

    // Groups and cards follow the sidebar order (screens arrive sorted like the menu)
    const grouped = useMemo(() => {
        const term = search.trim().toLowerCase()
        const filtered = term
            ? manuals.filter(m =>
                m.title.toLowerCase().includes(term) ||
                m.module.toLowerCase().includes(term) ||
                (m.summary ?? "").toLowerCase().includes(term))
            : manuals

        const sorted = filtered
            .map(m => ({ manual: m, order: sidebarOrder(m, screens) }))
            .sort((a, b) =>
                a.order - b.order ||
                (a.manual.type === b.manual.type ? 0 : a.manual.type === "screen" ? -1 : 1) ||
                a.manual.title.localeCompare(b.manual.title))

        const map = new Map<string, ManualMeta[]>()
        for (const { manual } of sorted) {
            if (!map.has(manual.module)) map.set(manual.module, [])
            map.get(manual.module)!.push(manual)
        }
        return Array.from(map.entries())
    }, [manuals, screens, search])

    return (
        <div className="page-content">
            <Container fluid style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
                <Breadcrumb title="Manuals" breadcrumbItem="Manuals" />

                <Row>
                    <Col md={6} lg={4}>
                        <Input
                            type="search"
                            placeholder="Search manuals..."
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                        />
                    </Col>
                </Row>

                {isLoading && !isLoaded && <Spinner color="primary" />}

                {isLoaded && grouped.length === 0 && (
                    <p className="text-muted">No manuals found.</p>
                )}

                {grouped.map(([group, items]) => (
                    <div key={group}>
                        <h5 className="mb-3">{group}</h5>
                        <Row className="g-3">
                            {items.map(m => (
                                <Col key={m.slug} sm={6} lg={4} xl={3}>
                                    <Link to={manualViewUrl(m.slug)} className="manual-card-link">
                                        <Card className="manual-card h-100 mb-0">
                                            <CardBody>
                                                <h6 className="manual-card__title mb-2">
                                                    <i className={`bx ${m.type === "guide" ? "bx-list-ol" : "bx-book-open"} me-2`}></i>
                                                    {m.title}
                                                </h6>
                                                {m.summary && <p className="text-muted mb-2">{m.summary}</p>}
                                                {m.lastUpdated && (
                                                    <small className="text-muted">Updated {m.lastUpdated}</small>
                                                )}
                                            </CardBody>
                                        </Card>
                                    </Link>
                                </Col>
                            ))}
                        </Row>
                    </div>
                ))}
            </Container>
        </div>
    )
}

export default withRouter(ManualsPage)
